<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="flex flex-center bg-gradient">
        <div class="register-container">
          <!-- Logo e Título -->
          <div class="text-center q-mb-xl">
            <q-icon size="110px">
              <img src="../assets/icon2.png" alt="">
            </q-icon>
            <h4 class="text-h4 text-weight-bold q-mb-sm q-mt-none">Criar Conta</h4>
            <p class="text-grey-7">Preencha os dados para se registrar</p>
          </div>

          <!-- Card de Registro -->
          <q-card class="register-card q-pa-lg shadow-10">
            <q-card-section>
              <!-- Mensagem de Sucesso -->
              <q-banner v-if="success" class="bg-positive text-white q-mb-lg banner-modern" rounded>
                <template v-slot:avatar>
                  <q-icon name="check_circle" color="white" />
                </template>
                <div class="text-weight-medium">Conta criada com sucesso!</div>
                <div class="text-caption q-mt-xs">Foi enviado um email para {{ emailActivate }}, para concluir o registo.</div>
              </q-banner>

              <!-- Mensagem de Erro -->
              <q-banner v-if="submitError" class="bg-negative text-white q-mb-lg banner-modern" rounded>
                <template v-slot:avatar>
                  <q-icon name="error" color="white" />
                </template>
                {{ submitError }}
              </q-banner>

              <q-form @submit="onSubmit" @reset="onReset" class="q-gutter-md">
                <!-- Nome -->
                <q-input
                  v-model="formData.name"
                  label="Nome Completo"
                  placeholder="Digite seu nome completo"
                  outlined
                  rounded
                  lazy-rules
                  :rules="nameRules"
                >
                  <template v-slot:prepend>
                    <q-icon name="person" />
                  </template>
                </q-input>

                <!-- Email -->
                <q-input
                  v-model="formData.email"
                  type="email"
                  label="Email"
                  placeholder="seu@email.com"
                  outlined
                  rounded
                  lazy-rules
                  :rules="emailRules"
                >
                  <template v-slot:prepend>
                    <q-icon name="mail" />
                  </template>
                </q-input>

                <!-- Senha -->
                <q-input
                  v-model="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  label="Senha"
                  placeholder="Mínimo 6 caracteres"
                  outlined
                  rounded
                  lazy-rules
                  :rules="passwordRules"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showPassword ? 'visibility_off' : 'visibility'"
                      class="cursor-pointer"
                      @click="showPassword = !showPassword"
                    />
                  </template>
                </q-input>

                <!-- Confirmação de Senha -->
                <q-input
                  v-model="formData.password_confirmation"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  label="Confirmar Senha"
                  placeholder="Repita a senha"
                  outlined
                  rounded
                  lazy-rules
                  :rules="passwordConfirmRules"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showPasswordConfirm ? 'visibility_off' : 'visibility'"
                      class="cursor-pointer"
                      @click="showPasswordConfirm = !showPasswordConfirm"
                    />
                  </template>
                </q-input>

                <!-- Botão Submit -->
                <q-btn
                  type="submit"
                  color="primary"
                  :loading="loading"
                  :disable="loading"
                  unelevated
                  rounded
                  no-caps
                  size="lg"
                  class="full-width q-mt-md register-btn"
                >
                  <template v-slot:default>
                    <div class="row items-center no-wrap justify-center">
                      <span class="text-weight-medium">Criar Conta</span>
                      <q-icon name="person_add" size="20px" class="q-ml-sm" />
                    </div>
                  </template>
                  <template v-slot:loading>
                    <q-spinner-dots class="on-left" />
                    <span>Registrando...</span>
                  </template>
                </q-btn>
              </q-form>
            </q-card-section>

            <!-- Divider -->
            <q-separator class="q-my-md" />

            <!-- Link para Login -->
            <q-card-section class="text-center">
              <p class="text-grey-7 q-mb-none">
                Já tem uma conta?
                <q-btn
                  flat
                  dense
                  color="primary"
                  label="Fazer login"
                  @click="$router.push('/')"
                  class="text-weight-bold"
                  no-caps
                />
              </p>
            </q-card-section>
          </q-card>

          <!-- Footer -->
          <div class="text-center q-mt-md">
            <p class="text-grey-6 text-caption">
              © {{ new Date().getFullYear() }} Plataforma Imobiliária. Um produto da <a href="https://marrar.co.mz" target="__blank" style="text-decoration: none;">Marrar, LDA</a>.
            </p>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script>
import { ref } from 'vue';
import store from '@/vuex/store';

export default {
  name: 'UserRegistration',

  setup() {

    const formData = ref({
      name: '',
      email: '',
      password: '',
      password_confirmation: ''
    });

    const showPassword = ref(false);
    const showPasswordConfirm = ref(false);
    const loading = ref(false);
    const success = ref(false);
    const submitError = ref('');
    const emailActivate = ref('');

    // Regras de validação
    const nameRules = [
      val => (val && val.length > 0) || 'Nome é obrigatório',
      val => (val && val.length >= 3) || 'Nome deve ter pelo menos 3 caracteres'
    ];

    const emailRules = [
      val => (val && val.length > 0) || 'Email é obrigatório',
      val => {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(val) || 'Email inválido';
      }
    ];

    const passwordRules = [
      val => (val && val.length > 0) || 'Senha é obrigatória',
      val => (val && val.length >= 6) || 'Senha deve ter pelo menos 6 caracteres'
    ];

    const passwordConfirmRules = [
      val => (val && val.length > 0) || 'Confirmação de senha é obrigatória',
      val => val === formData.value.password || 'As senhas não correspondem'
    ];

    const onSubmit = async () => {
      loading.value = true;
      success.value = false;
      submitError.value = '';

      try {

        await store.dispatch('auth/autoRegister', {
          name: formData.value.name,
          email: formData.value.email,
          password: formData.value.password,
          password_confirmation: formData.value.password_confirmation
        });

        // Sucesso
        emailActivate.value = formData.value.email;
        success.value = true;


        // Limpa o formulário
        formData.value = {
          name: '',
          email: '',
          password: '',
          password_confirmation: ''
        };

      } catch (error) {

        console.log(error)

        submitError.value = Object.values(error.response.data.errors)[0][0] || 'Erro ao registrar. Tente novamente.';


      } finally {
        loading.value = false;
      }
    };

    const onReset = () => {
      formData.value = {
        name: '',
        email: '',
        password: '',
        password_confirmation: ''
      };
      success.value = false;
      submitError.value = '';
    };

    return {
      formData,
      showPassword,
      showPasswordConfirm,
      emailActivate,
      loading,
      success,
      submitError,
      nameRules,
      emailRules,
      passwordRules,
      passwordConfirmRules,
      onSubmit,
      onReset
    };
  }
};
</script>

<style scoped>
.bg-gradient {
  background: linear-gradient(135deg, #e3f2fd 0%, #ffffff 50%, #e8eaf6 100%);
  min-height: 100vh;
}

.register-container {
  width: 100%;
  max-width: 450px;
  padding: 20px;
}

.register-card {
  border-radius: 16px;
  border: 1px solid #e0e0e0;
}

.shadow-10 {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.register-btn {
  letter-spacing: 0.5px;
  padding: 12px 24px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.register-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.35);
}

.banner-modern {
  border-radius: 12px;
}

@media (max-width: 600px) {
  .register-container {
    max-width: 100%;
  }
}
</style>
